/** URL-safe slug from a title */
export function slugifyTitle(title) {
  return String(title || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");
}

/**
 * Rich catalog for service / disease detail pages (symptoms, pain, imagery).
 * Supabase `dental_problems` rows can override title/description/details/icon when merged in UI.
 */
export const DISEASE_CATALOG = [
  {
    slug: "tooth-decay",
    title: "Tooth Decay",
    description: "Cavities caused by bacteria damaging tooth enamel",
    details:
      "Tooth decay (dental caries) begins when acids from plaque dissolve enamel. Early decay may be reversible with fluoride; established cavities need cleaning and restoration. We focus on minimally invasive fillings, sealants, and prevention plans tailored to your diet and hygiene.",
    icon: "/Dental-Fillings-1-1.gif", // Tooth decay / cavities
    painLevel: 4,
    painLabel: "Mild to moderate",
    symptoms: [
      "Sensitivity to sweet, hot, or cold foods",
      "Visible holes or dark spots on teeth",
      "Pain when biting or lingering toothache",
      "Food getting stuck in certain areas",
      "Bad breath or unpleasant taste",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1606811971618-4486d14feca7?w=800&q=80&auto=format&fit=crop",
        alt: "Dental examination and preventive care",
      },
      {
        url: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&q=80&auto=format&fit=crop",
        alt: "Dental treatment setting",
      },
    ],
  },
  {
    slug: "gum-disease",
    title: "Gum Disease",
    description: "Infection of tissues surrounding teeth",
    details:
      "Gingivitis and periodontitis affect the gums and bone supporting your teeth. Early gum disease is often reversible with professional cleaning and improved home care. Advanced cases may need deep scaling, root planing, or periodontal therapy—we assess stage and risk to build the right plan.",
    icon: "/Gum-Treatment.gif", // Gum disease care
    painLevel: 5,
    painLabel: "Variable; often mild early on",
    symptoms: [
      "Red, swollen, or tender gums",
      "Bleeding when brushing or flossing",
      "Receding gums or longer-looking teeth",
      "Persistent bad breath",
      "Loose teeth in later stages",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80&auto=format&fit=crop",
        alt: "Dental professional with patient",
      },
      {
        url: "https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=800&q=80&auto=format&fit=crop",
        alt: "Dental hygiene and gum care",
      },
    ],
  },
  {
    slug: "tooth-sensitivity",
    title: "Tooth Sensitivity",
    description: "Pain when consuming hot or cold items",
    details:
      "Sensitivity often comes from exposed dentin, worn enamel, gum recession, or recent whitening. We identify the cause—whether erosion, cracks, or brushing habits—and treat with desensitizing agents, bonding, fluoride, or occlusal adjustments as needed.",
    icon: "/Laser-Treatment-1.gif", // Sensitivity (gentle care visual)
    painLevel: 6,
    painLabel: "Moderate (sharp, brief)",
    symptoms: [
      "Sharp pain with cold drinks or ice cream",
      "Discomfort with hot foods or beverages",
      "Pain when breathing cold air",
      "Sensitivity when brushing near the gum line",
      "Discomfort with sweet or acidic foods",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1559591935-c7cf07212d9c?w=800&q=80&auto=format&fit=crop",
        alt: "Dental care consultation",
      },
      {
        url: "https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=800&q=80&auto=format&fit=crop",
        alt: "Modern dental clinic",
      },
    ],
  },
  {
    slug: "root-canal-issues",
    title: "Root Canal Issues",
    description: "Infection in the tooth's nerve chamber",
    details:
      "When pulp inside the tooth is inflamed or infected, root canal therapy removes damaged tissue, disinfects the canal, and seals the tooth—often saving it from extraction. Modern techniques and anesthesia make the procedure comfortable; most patients return to normal activities quickly.",
    icon: "/RCT.gif", // Root canal treatment
    painLevel: 8,
    painLabel: "Severe before treatment; much less after",
    symptoms: [
      "Severe toothache or throbbing pain",
      "Pain that wakes you at night",
      "Prolonged sensitivity to hot or cold",
      "Swelling or tenderness near the tooth",
      "Darkening of the tooth or a pimple on the gum",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80&auto=format&fit=crop",
        alt: "Dental equipment and care",
      },
      {
        url: "https://images.unsplash.com/photo-1629909615184-28f17d77f097?w=800&q=80&auto=format&fit=crop",
        alt: "Dental procedure environment",
      },
    ],
  },
  {
    slug: "misalignment",
    title: "Misalignment",
    description: "Crooked or crowded teeth",
    details:
      "Malocclusion affects bite, cleaning, speech, and confidence. We evaluate crowding, spacing, and jaw relationship to recommend braces, clear aligners, or other orthodontic options—always with your lifestyle and goals in mind.",
    icon: "/Invisible-Braces-1.gif", // Clear aligners / misalignment
    painLevel: 3,
    painLabel: "Usually low; jaw strain possible",
    symptoms: [
      "Crowded or overlapping teeth",
      "Gaps between teeth",
      "Difficulty flossing certain areas",
      "Jaw pain or clicking from bite imbalance",
      "Self-consciousness about smile appearance",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=800&q=80&auto=format&fit=crop",
        alt: "Smiling patient dental care",
      },
      {
        url: "https://images.unsplash.com/photo-1445527815219-ecbfecdb92a2?w=800&q=80&auto=format&fit=crop",
        alt: "Healthy smile focus",
      },
    ],
  },
  {
    slug: "tooth-loss",
    title: "Tooth Loss",
    description: "Missing teeth affecting function and appearance",
    details:
      "Missing teeth can shift bite, cause bone loss, and impact nutrition. We discuss implants, fixed bridges, and removable dentures based on bone health, budget, and aesthetics—restoring function and a natural-looking smile.",
    icon: "/Dental-Implants.gif", // Implants for tooth loss
    painLevel: 2,
    painLabel: "Low at site if healed; bite issues may ache",
    symptoms: [
      "Visible gaps or missing teeth",
      "Difficulty chewing certain foods",
      "Shifting of neighboring teeth",
      "Bone loss or sunken appearance over time",
      "Speech changes with front tooth loss",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&q=80&auto=format&fit=crop",
        alt: "Dental professional consultation",
      },
      {
        url: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80&auto=format&fit=crop",
        alt: "Dental care team",
      },
    ],
  },
];

export function getDiseaseBySlug(slug) {
  if (!slug) return null;
  return DISEASE_CATALOG.find((d) => d.slug === slug) || null;
}

/** Merge API row with catalog so detail pages stay rich when Supabase has partial fields */
export function mergeProblemRow(row) {
  const title = row.title || row.name || "";
  const catalog = DISEASE_CATALOG.find(
    (c) => c.slug === row.slug || c.title.toLowerCase() === String(title).toLowerCase()
  );
  const slug = row.slug || catalog?.slug || slugifyTitle(title);
  const base = catalog || getDiseaseBySlug(slug);
  return {
    slug,
    title: title || base?.title || "Dental condition",
    description: row.description || base?.description || "",
    details: row.details || base?.details || "",
    icon: row.icon || base?.icon || "/Dental-Fillings-1-1.gif",
    painLevel: row.pain_level ?? row.painLevel ?? base?.painLevel ?? 5,
    painLabel: row.pain_label ?? row.painLabel ?? base?.painLabel ?? "Varies",
    symptoms: Array.isArray(row.symptoms)
      ? row.symptoms
      : typeof row.symptoms === "string"
        ? row.symptoms.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
        : base?.symptoms || [],
    images: Array.isArray(row.images) && row.images.length
      ? row.images.map((img) => (typeof img === "string" ? { url: img, alt: title } : img))
      : base?.images || [],
  };
}
